import React, { type ReactNode } from 'react';
import {
  MODE_BADGE_SIZE_MD,
  MODE_BADGE_TONE_NEUTRAL,
  MODE_BADGE_VARIANT_FLAT,
  MODE_BADGE_VARIANT_PILL,
  type ModeBadgeSize,
  type ModeBadgeTone,
  type ModeBadgeVariant,
} from './ModeBadge.constants';
import {
  Dot,
  Label,
  LeadingIcon,
  Root,
  TrailingIcon,
} from './ModeBadge.styled';

export interface ModeBadgeProps {
  /** Visible label, e.g. "Draft" / "Editing" / "Published". */
  children: ReactNode;
  /** Tone vocabulary mirrors `StatusPill` for 1:1 mapping. Defaults to `neutral`. */
  tone?: ModeBadgeTone;
  /** Pill size — `sm` for inline use, `md` (default) for standalone placement. */
  size?: ModeBadgeSize;
  /**
   * Visual treatment. `pill` (default) is the bordered pill used in
   * banners. `flat` is the transparent letter-spaced caps treatment
   * used inside the floating canvas toolbar (no dot by default — the
   * caps + tone colour carry the state signal).
   */
  variant?: ModeBadgeVariant;
  /**
   * Optional leading icon (e.g. a `MdEdit` glyph for "Editing"). Mutually
   * exclusive with `dot` — passing both renders the icon and drops the dot.
   */
  icon?: ReactNode;
  /**
   * Show a small filled-dot indicator before the label. Defaults to
   * `true` for `variant="pill"` and `false` for `variant="flat"` (the
   * flat treatment carries its own typographic emphasis and the dot
   * just adds clutter).
   */
  dot?: boolean;
  /**
   * Optional trailing icon — typically an `MdArrowDropDown` chevron when
   * the badge is the trigger for a mode-switcher menu. The molecule
   * doesn't render the menu itself; pair this with `onClick`.
   */
  trailingIcon?: ReactNode;
  /**
   * When provided, the badge renders as a `<button>` and becomes
   * focusable + clickable — typically used to open a mode-switcher menu.
   * Without it, the badge is a static `<span>`.
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * When the badge is interactive, this gives the button its accessible
   * name. Defaults to using the visible children when omitted.
   */
  ariaLabel?: string;
  /** When interactive, mirrors the open state of the menu it triggers. */
  isOpen?: boolean;
  className?: string;
  dataTestId?: string;
}

/**
 * Pill that communicates the **current mode / state** of an editor /
 * page / app — "Draft", "Editing", "Read-only", "v1.4". Visually distinct
 * from `StatusPill`: same tone vocabulary, but a tinted BORDER rather
 * than a tinted FILL, plus tabular numerals so version / count strings
 * line up nicely.
 *
 * When `onClick` is set the badge becomes a button — useful for the
 * "click to switch modes" pattern where the badge is the affordance for
 * a popover menu.
 *
 * @example
 *   // Static — header at the top of a page
 *   <ModeBadge tone="caution" dot>Draft</ModeBadge>
 *
 *   // Interactive — opens a mode switcher
 *   <ModeBadge
 *     tone="primary"
 *     trailingIcon={<MdArrowDropDown />}
 *     isOpen={menuOpen}
 *     onClick={openMenu}
 *   >
 *     Editing
 *   </ModeBadge>
 */
const ModeBadge: React.FC<ModeBadgeProps> = ({
  children,
  tone = MODE_BADGE_TONE_NEUTRAL,
  size = MODE_BADGE_SIZE_MD,
  variant = MODE_BADGE_VARIANT_PILL,
  icon,
  dot,
  trailingIcon,
  onClick,
  ariaLabel,
  isOpen,
  className,
  dataTestId = 'mode-badge',
}) => {
  // `flat` defaults dot off; `pill` defaults dot on. Either default can
  // be overridden by passing `dot` explicitly.
  const dotResolved = dot ?? variant !== MODE_BADGE_VARIANT_FLAT;
  // Icon supersedes the dot — the consumer chose a richer visual cue and
  // we don't want to render both.
  const showDot = dotResolved && !icon;
  const interactive = !!onClick;

  return (
    <Root
      as={interactive ? 'button' : 'span'}
      type={interactive ? 'button' : undefined}
      $tone={tone}
      $size={size}
      $variant={variant}
      $interactive={interactive}
      onClick={onClick}
      aria-label={interactive ? ariaLabel : undefined}
      // Emit the explicit boolean (true OR false) whenever this is a
      // toggle — so screen readers correctly announce "not pressed" in
      // the off state. Only leave `aria-pressed` undefined when this is
      // a non-toggle interactive trigger (no `isOpen` supplied at all).
      aria-pressed={
        interactive && isOpen !== undefined ? !!isOpen : undefined
      }
      aria-haspopup={interactive && trailingIcon ? 'menu' : undefined}
      aria-expanded={interactive && isOpen !== undefined ? isOpen : undefined}
      className={className}
      data-testid={dataTestId}
      data-tone={tone}
      data-size={size}
      data-variant={variant}
      data-interactive={interactive || undefined}
    >
      {icon && (
        <LeadingIcon
          aria-hidden="true"
          data-testid={`${dataTestId}__icon`}
        >
          {icon}
        </LeadingIcon>
      )}
      {showDot && (
        <Dot
          $tone={tone}
          aria-hidden="true"
          data-testid={`${dataTestId}__dot`}
        />
      )}
      <Label>{children}</Label>
      {trailingIcon && (
        <TrailingIcon
          aria-hidden="true"
          data-testid={`${dataTestId}__trailing-icon`}
        >
          {trailingIcon}
        </TrailingIcon>
      )}
    </Root>
  );
};

export default ModeBadge;
